import { has, forEach, isString, reduce, includes, startsWith } from 'lodash';
import querystringify from 'querystringify';
import { Base64 } from 'js-base64';

import { POST } from '../constants/requestMethods';
import { ROUTE_SSO_AUTH_LINK } from '../constants/pathnames';
import { API_SERVER_URL } from '../constants/api';
import {
  SSO_AUTH_TYPES,
  SSO_AUTH_KEYS,
  OIDC_SSO_TYPES,
  SUPPORTED_OIDC_SSO_TYPES,
  DATATYPE_MACRO_SETTINGS,
} from '../constants/auth';

import { generateCodeVerifier, generateCodeChallenge } from './utils/oidc';
import UIUtils from '../lib/UIUtils';

import { getCorsCompatibleURL } from '../lib/URLUtils';

const REDIRECT_URI = window.location.protocol + '//' + window.location.host + ROUTE_SSO_AUTH_LINK;

const OIDC_AUTH_URL_PARAM_PREFIX = 'oidc.auth_url_param.';
const OIDC_TOKEN_URL_PARAM_PREFIX = 'oidc.token_url_param.';

export const generateTokenIdentifiers = (isPkce = true, redirectTo = '/') => {
  let tokenIdentifiers = {};
  if (isPkce) {
    let oidc_code_verifier = generateCodeVerifier();
    tokenIdentifiers['pkce_code_verifier'] = oidc_code_verifier;
    tokenIdentifiers['pkce_code_challenge'] = generateCodeChallenge(oidc_code_verifier);
  }

  tokenIdentifiers['nonce'] = UIUtils.generateNonce();
  //TODO: Replace this with redirect URL if available
  tokenIdentifiers['state'] = redirectTo;

  return tokenIdentifiers;
};

export const beginIdpAuth = (idpConfigInfo = {}, idpTokenParams = {}) => {
  const authMode = idpConfigInfo[SSO_AUTH_KEYS.authMode];

  if (includes(SUPPORTED_OIDC_SSO_TYPES, authMode)) {
    //if valid info route to idp auth url
    const authURL = getSSOAuthURL(authMode, idpConfigInfo, {
      [DATATYPE_MACRO_SETTINGS.MACRO_KEYS.oidc_code_challenge]:
        idpTokenParams['pkce_code_challenge'],
      [DATATYPE_MACRO_SETTINGS.MACRO_KEYS.nonce]: idpTokenParams['nonce'],
    });

    window.location.replace(authURL);
  } else if (authMode === SSO_AUTH_TYPES.saml) {
    const UI_BASE_URL = window.location.protocol + '//' + window.location.host;
    const ssoBaseURL = UI_BASE_URL + '/api/samlLogin';
    // const acsUrl = replace(idpConfigInfo['nexla.sso_token_url'],API_SERVER_URL, UI_BASE_URL);
    const currentTime = new Date().toISOString();
    const acsUrl = has(idpConfigInfo, SSO_AUTH_KEYS.saml_acs_url)
      ? idpConfigInfo[SSO_AUTH_KEYS.saml_acs_url]
      : UI_BASE_URL + '/api/sso';

    const isAzureSSO =
      has(idpConfigInfo, SSO_AUTH_KEYS.idp) && idpConfigInfo[SSO_AUTH_KEYS.idp] === 'azure'
        ? true
        : false;

    const ssoRequestID = isAzureSSO ? 'x' + UIUtils.generateNonce() : UIUtils.generateNonce();

    const SAMLRequest = isAzureSSO
      ? `<samlp:AuthnRequest xmlns:samlp="urn:oasis:names:tc:SAML:2.0:protocol"
xmlns:saml="urn:oasis:names:tc:SAML:2.0:assertion"
ID="${ssoRequestID}"
Version="2.0"
ProviderName="Nexla"
IssueInstant="${currentTime}"
ProtocolBinding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST"
AssertionConsumerServiceURL="${acsUrl}">
<saml:Issuer>${API_SERVER_URL}</saml:Issuer>
<samlp:NameIDPolicy Format="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress"
AllowCreate="true" />
</samlp:AuthnRequest>`
      : `<samlp:AuthnRequest xmlns:samlp="urn:oasis:names:tc:SAML:2.0:protocol"
xmlns:saml="urn:oasis:names:tc:SAML:2.0:assertion"
ID="${ssoRequestID}"
Version="2.0"
ProviderName="Nexla"
IssueInstant="${currentTime}"
ProtocolBinding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST"
AssertionConsumerServiceURL="${acsUrl}">
<saml:Issuer>${API_SERVER_URL}</saml:Issuer>
<samlp:NameIDPolicy Format="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress"
AllowCreate="true" />
<samlp:RequestedAuthnContext Comparison="exact">
<saml:AuthnContextClassRef>urn:oasis:names:tc:SAML:2.0:ac:classes:PasswordProtectedTransport</saml:AuthnContextClassRef>
</samlp:RequestedAuthnContext>
</samlp:AuthnRequest>`;

    const requestData = Base64.encode(SAMLRequest);

    let ssoUrlParams = {
      sso_id: idpConfigInfo[SSO_AUTH_KEYS.saml_sso_id],
      sso_url: idpConfigInfo[SSO_AUTH_KEYS.saml_sso_url],
      acs_url: acsUrl,
      request_data: requestData,
      // relay_state: idpConfigInfo[SSO_AUTH_KEYS.nexla_token_url]
    };

    if (
      !has(idpConfigInfo, SSO_AUTH_KEYS.nexla_token_url_redirect) ||
      idpConfigInfo[SSO_AUTH_KEYS.nexla_token_url_redirect] === false
    ) {
      ssoUrlParams['relay_state'] = idpConfigInfo[SSO_AUTH_KEYS.nexla_token_url];
    }
    const ssoLoginUrl = ssoBaseURL + querystringify.stringify(ssoUrlParams, true);

    window.location.replace(ssoLoginUrl);
  } else {
    //else route to nexla handler path that will close out login session.
    const authRedirectURL =
      REDIRECT_URI +
      '?response_type=error&reason=Invalid SSO configuration. Please check configuration with Nexla administrator';
    window.location.replace(authRedirectURL);
  }
};

export const fetchIdpToken = async (
  idpConfigInfo = {},
  idpAuthParams = {},
  idpTokenParams = {},
) => {
  const authMode = idpConfigInfo[SSO_AUTH_KEYS.authMode];

  const configTokenUrl = includes(OIDC_SSO_TYPES, authMode)
    ? idpConfigInfo[SSO_AUTH_KEYS.oidc_token_url]
    : '';

  const isAzureSSO =
    has(idpConfigInfo, SSO_AUTH_KEYS.idp) && idpConfigInfo[SSO_AUTH_KEYS.idp] === 'azure'
      ? true
      : false;

  //do not do cors for azure pkce cos it will fail

  const tokenURL =
    authMode === SSO_AUTH_TYPES.oidc_pkce && isAzureSSO
      ? configTokenUrl
      : getCorsCompatibleURL(configTokenUrl);

  const tokenParams = getParamsFromConfig(
    idpConfigInfo,
    OIDC_TOKEN_URL_PARAM_PREFIX,
    {
      [DATATYPE_MACRO_SETTINGS.MACRO_KEYS.oidc_code_challenge]:
        idpAuthParams['pkce_code_challenge'],
      [DATATYPE_MACRO_SETTINGS.MACRO_KEYS.oidc_code_verifier]: idpAuthParams['pkce_code_verifier'],
    },
    ['code=' + idpTokenParams['code']],
  );

  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    Accept: 'application/json',
  };

  const response = await fetch(tokenURL, {
    method: POST,
    body: tokenParams,
    headers,
  });

  if (!response.ok || response.status < 200 || response.status >= 300) {
    throw new Error(has(response, 'statusText') ? response.statusText : 'Failed to Authenticate');
  }

  let responseJSON = await response.json();
  if (authMode === SSO_AUTH_TYPES.oidc_pkce && isAzureSSO && has(responseJSON, 'access_token')) {
    //strip out access_token from response for azure pkce cos it cannot be decoded
    delete responseJSON['access_token'];
  }
  return responseJSON;
};

function getSSOAuthURL(authMode = SSO_AUTH_TYPES.none, serviceInfo = {}, generatedParams = {}) {
  let authURL = null;

  switch (authMode) {
    case SSO_AUTH_TYPES.oidc_pkce:
    case SSO_AUTH_TYPES.oidc_implicit:
      const baseAuthURL = serviceInfo[SSO_AUTH_KEYS.oidc_auth_url] || '';
      const baseURLParams = getParamsFromConfig(
        serviceInfo,
        OIDC_AUTH_URL_PARAM_PREFIX,
        generatedParams,
      );
      authURL = `${baseAuthURL}?${baseURLParams}`;

      break;

    default:
      break;
  }

  return authURL;
}

function getParamsFromConfig(configInfo, paramPrefix, generatedParams = null, extraParams = null) {
  let params = ['redirect_uri=' + encodeURIComponent(REDIRECT_URI)];

  if (extraParams) {
    params = params.concat(extraParams);
  }

  forEach(configInfo, (value, key) => {
    if (!key.startsWith(paramPrefix)) {
      return;
    }

    const paramName = key.substring(paramPrefix.length);
    const paramValue = evaluateMacros(value, configInfo, generatedParams);

    params.push(paramName + '=' + encodeURIComponent(paramValue));
  });

  return params.join('&');
}

function evaluateMacros(value, data, generatedParams = {}) {
  if (isString(value)) {
    // evaluate macros in the param value
    const macros = value.match(/\$\{([\w.]+)\}/g);
    return reduce(
      macros,
      (acc, macro) => {
        const macroParamName = macro.slice(2, -1);
        let replacementValue = null;

        if (startsWith(macroParamName, DATATYPE_MACRO_SETTINGS.MACRO_PREFIX)) {
          //custom macro.
          const dataTypeMacro = macroParamName.slice(DATATYPE_MACRO_SETTINGS.MACRO_PREFIX.length);

          switch (dataTypeMacro) {
            case DATATYPE_MACRO_SETTINGS.MACRO_KEYS.nonce:
              if (has(generatedParams, DATATYPE_MACRO_SETTINGS.MACRO_KEYS.nonce)) {
                replacementValue = generatedParams[DATATYPE_MACRO_SETTINGS.MACRO_KEYS.nonce];
              } else {
                replacementValue = UIUtils.generateNonce();
              }
              break;

            case DATATYPE_MACRO_SETTINGS.MACRO_KEYS.oidc_code_verifier:
              if (has(generatedParams, DATATYPE_MACRO_SETTINGS.MACRO_KEYS.oidc_code_verifier)) {
                replacementValue =
                  generatedParams[DATATYPE_MACRO_SETTINGS.MACRO_KEYS.oidc_code_verifier];
              }
              break;

            case DATATYPE_MACRO_SETTINGS.MACRO_KEYS.oidc_code_challenge:
              if (has(generatedParams, DATATYPE_MACRO_SETTINGS.MACRO_KEYS.oidc_code_challenge)) {
                replacementValue =
                  generatedParams[DATATYPE_MACRO_SETTINGS.MACRO_KEYS.oidc_code_challenge];
              }
              break;

            default:
              break;
          }
        } else if (has(data, macroParamName)) {
          //replace macro placeholder with macroValue in data
          replacementValue = data[macroParamName];
          return acc.replace(macro);
        }

        return replacementValue ? acc.replace(macro, replacementValue) : acc;
      },
      value,
    );
  }

  return value;
}
